import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import EmailChangeModal from '../../../components/settings/EmailChangeModal';
import InfoModal from '../../../components/ui/InfoModal';

// Import admin-specific tab components
import AdminSecurityTab from './components/AdminSecurityTab';
import AdminAccountSettingsTab from './components/AdminAccountSettingsTab';

// Import utilities
import { adminProfileService } from '../../../utils/adminProfileService';
import { profilePictureService } from '../../../utils/profilePictureService';

const AdminProfileSettings = () => {
  const { userProfile, updateProfile, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get tab from URL hash, default to 'account'
  const getTabFromUrl = () => {
    const hash = location.hash.replace('#', '');
    const validTabs = ['account', 'security'];
    return validTabs.includes(hash) ? hash : 'account';
  };
  
  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(getTabFromUrl);
  const [fieldErrors, setFieldErrors] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Email change modal state
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
  const [emailChangeNotification, setEmailChangeNotification] = useState('');
  
  // Use ref to track the last processed profile ID to prevent infinite loops
  const lastProfileIdRef = useRef(null);
  
  // Initialize profile data with admin-specific defaults
  const [profileData, setProfileData] = useState({
    // Basic fields
    full_name: '',
    email: '',
    phone: '',
    location: '',
    bio: '',
    
    // Admin-specific fields
    admin_level: 'moderator', // 'admin' or 'moderator'
    admin_permissions: [],
    last_login: null,
    is_active: true,
    
    // Security settings
    security_settings: {
      two_factor_auth: false,
      suspicious_activity_alerts: true,
      device_tracking: true,
      password_expiry_days: 90,
      allow_multiple_sessions: true,
      require_verification_for_profile_changes: false
    },
    security_questions: []
  });

  const tabs = useMemo(() => [
    { id: 'account', label: 'Account Settings', icon: 'Settings' },
    { id: 'security', label: 'Security', icon: 'Lock' }
  ], []);

  // Handler to change tab and update URL
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    navigate(`#${tabId}`, { replace: true });
  }, [navigate]);

  // Sync activeTab with URL hash changes
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    const validTabs = ['account', 'security'];
    if (validTabs.includes(hash) && hash !== activeTab) {
      setActiveTab(hash);
    }
  }, [location.hash]);

  // Load user profile data on mount - prevent infinite loops
  useEffect(() => {
    if (userProfile && userProfile.id && lastProfileIdRef.current !== userProfile.id) {
      setProfileData(prevData => {
        const normalizedProfile = { ...prevData };
        
        // Normalize all data from userProfile
        Object.keys(userProfile).forEach(key => {
          if (userProfile[key] !== null && userProfile[key] !== undefined) {
            // Special handling for security_settings
            if (key === 'security_settings') {
              if (userProfile[key] && typeof userProfile[key] === 'object') {
                normalizedProfile[key] = {
                  ...prevData.security_settings,
                  ...userProfile[key]
                };
              } else {
                normalizedProfile[key] = prevData.security_settings;
              }
            }
            else if (key === 'security_questions') {
              normalizedProfile[key] = userProfile[key] || [];
            }
            else {
              normalizedProfile[key] = userProfile[key];
            }
          }
        });
        
        return normalizedProfile;
      });
      lastProfileIdRef.current = userProfile.id;
    }
  }, [userProfile]);

  // Enhanced input change handler
  const handleInputChange = useCallback((field, value) => {
    setProfileData(prev => {
      const newData = { ...prev };
      newData[field] = value;
      return newData;
    });
    setHasUnsavedChanges(true);
  }, []);

  // Form save handler
  const handleSaveProfile = useCallback(async () => {
    // Clear previous errors
    setError('');
    setFieldErrors({});
    setIsSuccess(false);
    
    // Validate required fields
    const errors = {};
    let hasValidationErrors = false;

    const requiredFields = ['email', 'full_name'];
    requiredFields.forEach(field => {
      const value = profileData[field];
      if (!value || (typeof value === 'string' && !value.trim())) {
        errors[field] = `${field.replace('_', ' ')} is required`;
        hasValidationErrors = true;
      }
    });

    if (hasValidationErrors) {
      setFieldErrors(errors);
      setError('Please fill in all required fields.');
      return;
    }

    setIsLoading(true);

    try {
      // Use admin profile service to update
      const result = await adminProfileService.updateAdminProfile(user.id, profileData);

      if (result?.success) {
        setIsSuccess(true);
        setHasUnsavedChanges(false);
        setTimeout(() => setIsSuccess(false), 3000);
      } else {
        const errorMessage = result?.error || 'Failed to update profile.';
        setError(errorMessage);
      }
    } catch (err) {
      const errorMessage = err?.message || 'An unexpected error occurred.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [profileData, user]);

  // Email change handler
  const handleEmailChangeInitiated = useCallback((message) => {
    setEmailChangeNotification(message);
    setShowEmailChangeModal(false);
    setTimeout(() => setEmailChangeNotification(''), 10000);
  }, []);

  // Profile picture upload handler
  const handleProfilePictureUpload = useCallback(async (file) => {
    try {
      const result = await profilePictureService.uploadProfilePicture(user.id, file);
      
      if (result.success) {
        setProfileData(prev => ({
          ...prev,
          profile_picture: result.url
        }));
        setHasUnsavedChanges(true);
        return { success: true, url: result.url, immediate: result.immediate };
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Profile picture upload error:', error);
      return { success: false, error: error.message };
    }
  }, [user]);

  // Render current tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'account':
        return (
          <AdminAccountSettingsTab 
            profileData={profileData}
            onInputChange={handleInputChange}
            onProfilePictureUpload={handleProfilePictureUpload}
            fieldErrors={fieldErrors}
          />
        );
      case 'security':
        return (
          <AdminSecurityTab 
            profileData={profileData}
            onInputChange={handleInputChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="admin-profile-settings-container">
      <style>{`
        .admin-profile-settings-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
        }

        .admin-profile-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
          border-bottom: 2px solid #e5e7eb;
          padding-bottom: 20px;
        }

        .admin-profile-header h1 {
          font-size: 28px;
          font-weight: bold;
          color: #1f2937;
          margin: 0;
        }

        .admin-profile-tabs {
          display: flex;
          gap: 10px;
          margin-bottom: 30px;
          border-bottom: 1px solid #e5e7eb;
          flex-wrap: wrap;
        }

        .admin-profile-tab-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          border: none;
          background: none;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: #6b7280;
          border-bottom: 3px solid transparent;
          transition: all 0.2s ease;
        }

        .admin-profile-tab-button:hover {
          color: #1f2937;
          background-color: #f9fafb;
        }

        .admin-profile-tab-button.active {
          color: #3b82f6;
          border-bottom-color: #3b82f6;
        }

        .admin-profile-content {
          background: white;
          border-radius: 8px;
          padding: 30px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          margin-bottom: 20px;
        }

        .admin-profile-actions {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px solid #e5e7eb;
        }

        .admin-profile-alert {
          padding: 12px 16px;
          border-radius: 6px;
          margin-bottom: 20px;
          font-size: 14px;
        }

        .admin-profile-alert.success {
          background-color: #dcfce7;
          color: #166534;
          border-left: 4px solid #22c55e;
        }

        .admin-profile-alert.error {
          background-color: #fee2e2;
          color: #991b1b;
          border-left: 4px solid #ef4444;
        }

        .admin-profile-alert.info {
          background-color: #dbeafe;
          color: #0c4a6e;
          border-left: 4px solid #3b82f6;
        }
      `}</style>

      <div className="admin-profile-header">
        <h1>Admin Profile Settings</h1>
        <div style={{ fontSize: '12px', color: '#6b7280' }}>
          {userProfile?.role === 'admin' ? '👑 Administrator' : '⚙️ Moderator'}
        </div>
      </div>

      {emailChangeNotification && (
        <div className="admin-profile-alert info">
          {emailChangeNotification}
        </div>
      )}

      {error && (
        <div className="admin-profile-alert error">
          {error}
        </div>
      )}

      {isSuccess && (
        <div className="admin-profile-alert success">
          Profile updated successfully!
        </div>
      )}

      <div className="admin-profile-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`admin-profile-tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
          >
            <Icon name={tab.icon} size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="admin-profile-content">
        {renderTabContent()}
      </div>

      <div className="admin-profile-actions">
        <Button
          variant="secondary"
          onClick={() => {
            setHasUnsavedChanges(false);
            navigate('/admin/dashboard');
          }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSaveProfile}
          disabled={!hasUnsavedChanges || isLoading}
          loading={isLoading}
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {showEmailChangeModal && (
        <EmailChangeModal
          currentEmail={profileData.email}
          onClose={() => setShowEmailChangeModal(false)}
          onSuccess={handleEmailChangeInitiated}
        />
      )}
    </div>
  );
};

export default AdminProfileSettings;
